/* eslint-disable react/jsx-props-no-spreading */
/**
 * @file
 *
 * Verify by Phone or E-mail and ZIP and DOB form.
 * Used in "Other" option on to Verify Account Page
 *
 * example:
 * <OtherForm/>
 */
import { zodResolver } from '@hookform/resolvers/zod'
import { createRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha-enterprise'
import { Controller, useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import { RECAPTCHA_CLIENT_KEY } from '../../../config/config'
import { MDVIP_VERIFY_ACCOUNT_DEFAULT_ERROR_HTML, MDVIP_VERIFY_ACCOUNT_ERROR_MESSAGE } from '../../../Constants'
import useAuthenticateContext from '../../../hooks/useAuthenticate'
import { captureMessage, initLogRocket } from '../../../utils/logrocket'
import { phoneMask, zipMask } from '../../../utils/masks'
import { dobPattern, phonePattern, zipPattern } from '../../../utils/patterns'
import { ContactMDVIPMessage } from '../../ContactMDVIPMessage'
import { toast } from '../../CustomToastMessage/toast'
import Input from '../../form-elements/Input'
import InputDate from '../../form-elements/Input/InputDate'
import LoadingSpinner from '../../misc/Spinners/LoadingSpinner'
import FormContinueButton from '../FormContinueButton'
import styles from './OtherForm.module.scss'

const otherFormSchema = z
  .object({
    phone: z.string().refine((value) => !value || phonePattern.test(value), {
      message: 'Please enter a valid Phone Number.',
    }),
    zipCode: z.string().refine((value) => !value || zipPattern.test(value), {
      message: 'Please enter a valid Zip Code.',
    }),
    dob: z
      .string()
      .nonempty({ message: 'Please enter a valid Date of Birth.' })
      .refine((value) => dobPattern.test(value), {
        message: 'Please enter a valid Date of Birth.',
      }),
  })
  .refine((data) => data.phone || data.zipCode, {
    message: 'Either phone number or zip code is required.',
    path: ['zipCode'],
  })

interface OtherFormProps {
  phone: string
  zipCode: string
  dob: string
}

const OtherForm = () => {
  const { loading, setParams } = useAuthenticateContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const expired = searchParams.get('expired') === 'true'

  const {
    handleSubmit,
    control,
    formState: { isValid },
    reset,
    trigger,
  } = useForm<OtherFormProps>({
    mode: 'onTouched',
    resolver: zodResolver(otherFormSchema),
    defaultValues: { phone: '', dob: '', zipCode: '' },
  })

  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)

  const [message, setMessage] = useState('')
  const captchaRef = createRef<typeof ReCAPTCHA>()

  const isFormComplete = isValid && (!RECAPTCHA_CLIENT_KEY || recaptchaToken) && !expired && !message

  const resetRecaptcha = () => {
    captchaRef.current?.reset()
  }
  const resetForm = () => {
    reset()
    resetRecaptcha()
  }

  const onSubmit = async (data: OtherFormProps) => {
    const { phone, zipCode, dob } = data

    if (phone.length === 0) {
      await initLogRocket('zipCode', `${zipCode}-${new Date().getTime()}`)
    } else if (zipCode.length === 0) {
      await initLogRocket('phone', phone)
    } else {
      await initLogRocket('zipCode-phone', `${zipCode}:${phone}`)
    }

    const onAuthenticateError = () => {
      const displayMessage = MDVIP_VERIFY_ACCOUNT_DEFAULT_ERROR_HTML

      captureMessage(displayMessage, {
        extra: {
          other: zipCode,
          error: displayMessage,
          pageName: 'Other Form',
        },
      })
      setMessage(displayMessage)
      toast(<ContactMDVIPMessage initialMessage={MDVIP_VERIFY_ACCOUNT_ERROR_MESSAGE} />)
      resetForm()
    }

    // Send values to API.
    if (phone.length > 0) {
      setParams({
        phone,
        dob,
        recaptchaToken,
        onAuthenticateError,
      })
    } else {
      setParams({
        zipCode,
        dob,
        recaptchaToken,
        onAuthenticateError,
      })
    }
    setMessage('')
    resetRecaptcha()
  }

  return (
    <>
      {loading && <LoadingSpinner loading={loading} />}
      <form onSubmit={handleSubmit(onSubmit)} id="form_other">
        <div className={styles['required-text']}>Verify using either a phone number or zip code.</div>
        <Controller
          control={control}
          name="phone"
          render={({ field: { onChange, ref, ...rest }, fieldState: { error } }) => (
            <Input
              privateData
              label="Phone Number/Mobile/Work"
              inputMode="tel"
              requiredMessage={error?.message}
              onChange={(e) => {
                setSearchParams({})
                setMessage('')
                onChange(phoneMask(e.target.value))
                trigger('zipCode')
              }}
              maxLength={15}
              {...rest}
            />
          )}
        />

        <Controller
          control={control}
          name="zipCode"
          render={({ field: { onChange, ref, ...rest }, fieldState: { error } }) => (
            <Input
              label="Zip Code"
              requiredMessage={error?.message}
              onChange={(e) => {
                setSearchParams({})
                setMessage('')
                onChange(zipMask(e.target.value))
              }}
              {...rest}
            />
          )}
        />

        <div className={styles['required-text']}>Date of Birth is Required</div>
        <Controller
          name="dob"
          control={control}
          render={({ field: { onChange, ref, ...rest }, fieldState: { error } }) => (
            <InputDate
              privateData
              label="Date of Birth (MM/DD/YYYY)"
              requiredMessage={error?.message}
              onChange={(e) => {
                setSearchParams({})
                setMessage('')
                onChange(e.target.value)
              }}
              {...rest}
            />
          )}
        />
        {RECAPTCHA_CLIENT_KEY && (
          <ReCAPTCHA ref={captchaRef} sitekey={RECAPTCHA_CLIENT_KEY} onChange={setRecaptchaToken} />
        )}

        <FormContinueButton isFormComplete={!!isFormComplete} />
      </form>
    </>
  )
}

export default OtherForm
